import { useApolloQuery } from '@/gql/apolloWrapper';
import {
  GetCompanyDelinquencyQuery,
  useGetCompanyDelinquencyQuery,
} from '@/gql/generated';
import { ref, computed } from '@vue/composition-api';
import useCurrentUser from '@/hooks/useCurrentUser';
import { differenceInDays, addDays } from 'date-fns';
import { DelinquentStage } from '@/modules/Billing/hooks/useDelinquency.interfaces';

export const useDelinquency = () => {
  const LOCKED_DAYS_THRESHOLD = 30;
  const CANCELLATION_DAYS_THRESHOLD = 90;
  const { currentUser, companyId } = useCurrentUser();
  const delinquent = ref<Partial<
    GetCompanyDelinquencyQuery['getCompanyDelinquency']
  > | null>(null);
  const isAdmin = computed<boolean>(() => currentUser.value?.role === 'user');
  const delinquentDays = computed<number>(() =>
    delinquent.value?.delinquentSince
      ? differenceInDays(new Date(), new Date(delinquent.value.delinquentSince))
      : 0
  );

  // todo: move to the backend (https://bambee.atlassian.net/browse/BACKEND-591)
  const cancellationDate = computed<Date | null>(() => {
    if (!delinquent.value || !delinquent.value.delinquent) {
      return null;
    }

    const delinquentDate = new Date(delinquent.value.delinquentSince);

    return addDays(delinquentDate, CANCELLATION_DAYS_THRESHOLD);
  });

  const daysTillCancellation = computed<number>(() => {
    if (!cancellationDate.value) {
      return 0;
    }

    return differenceInDays(cancellationDate.value, new Date());
  });

  const isCloseToCancellation = computed<boolean>(
    () => CANCELLATION_DAYS_THRESHOLD - delinquentDays.value < 30
  );

  // todo: move to the backend (https://bambee.atlassian.net/browse/BACKEND-591)
  const lockoutDate = computed<Date | null>(() => {
    if (!delinquent.value || !delinquent.value.delinquent) {
      return null;
    }

    const delinquentDate = new Date(delinquent.value.delinquentSince);

    return addDays(delinquentDate, LOCKED_DAYS_THRESHOLD);
  });

  // todo: move to backend (https://bambee.atlassian.net/browse/BACKEND-591)
  const delinquentStage = computed<DelinquentStage | null>(() => {
    if (!delinquent.value?.delinquent) {
      return null;
    }

    if (delinquent.value?.lockout) {
      return DelinquentStage.LOCKED;
    }

    return DelinquentStage.NEW;
  });

  const { onResult } = useApolloQuery(useGetCompanyDelinquencyQuery, {
    data: {
      companyId: companyId.value,
    },
  });

  onResult((result) => {
    delinquent.value = result.getCompanyDelinquency;
  });

  return {
    delinquent,
    delinquentStage,
    delinquentDays,
    lockoutDate,
    isAdmin,
    isCloseToCancellation,
    cancellationDate, // will be moving to backend in the future
    daysTillCancellation,
  };
};

export default useDelinquency;
